// Mantine
import { Anchor, Select, Spoiler, Button, Modal } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useDisclosure } from '@mantine/hooks'

// React
import { useState } from 'react'

// UI
import Box from '../ui/Box'
import QuantityDisplay from '../ui/QuantityDisplay'

// User
import useMe from '@/hooks/useMe'

// Api
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Dayjs
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/fr'

dayjs.extend(relativeTime)
dayjs.locale('fr')

export default function ComponentSelling({
    component,
    loadComponent,
    variants,
}) {
    const { isAdmin } = useMe()

    const [isB2BDisallowed, setIsB2BDisallowed] = useState(
        component.is_b2b_disallowed
    )

    const [availabilityB2B, setAvailabilityB2B] = useState(
        component.availability_b2b
    )

    const [availabilityB2C, setAvailabilityB2C] = useState(
        component.availability_b2c
    )

    const { requestPatch, requestPost } = useAxiosApi()

    const onAvailabilityB2BChange = async (value) => {
        await requestPatch(`/components/${component.id}/availability`, {
            availability_b2b: value,
        })
        setAvailabilityB2B(value)
        await loadComponent()
    }

    const onAvailabilityB2CChange = async (value) => {
        await requestPatch(`/components/${component.id}/availability`, {
            availability_b2c: value,
        })
        setAvailabilityB2C(value)
        await loadComponent()
    }

    // Dispatch date
    const [openedDateModale, handlersDateModale] = useDisclosure(false)
    const [newDate, setNewDate] = useState(
        component.dispatch_date ? dayjs(component.dispatch_date) : null
    )

    const saveNewDate = async () => {
        try {
            await requestPatch(`/components/${component.id}`, {
                dispatch_date: newDate ? dayjs(newDate).format('YYYY-MM-DD') : null,
            })
            await requestPost('/shopify/sync-fulfill-at', {})
            await loadComponent()
            handlersDateModale.close()
        } catch {
            // The API hook displays the failure; keep the modal open for retry.
        }
    }

    if (component.shopify_product_variants.length === 0) {
        return null
    }

    // Get last availability changes
    const lastB2CChange = component.availability_history?.find(
        (h) => h.field === 'availability_b2c'
    )

    const lastB2BChange = component.availability_history?.find(
        (h) => h.field === 'availability_b2b'
    )

    return (
        <Box title="Préparation">
            <div className="flex flex-col gap-10">
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <Select
                            label="Vente B2C"
                            allowDeselect={false}
                            value={availabilityB2C}
                            onChange={onAvailabilityB2CChange}
                            width="xl"
                            data={[
                                {
                                    value: 'follow',
                                    label: 'Suivre la quantité',
                                },
                                {
                                    value: 'force_available',
                                    label: 'Continuer à vendre en cas de rupture',
                                },
                                {
                                    value: 'force_disabled',
                                    label: 'Rupture forcée',
                                },
                            ]}
                        />

                        {lastB2CChange && (
                            <span className="text-xs text-gray-500">
                                {dayjs(lastB2CChange.created_at).fromNow()} par{' '}
                                {lastB2CChange.user?.name}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-1">
                        <Select
                            label="Vente B2B"
                            allowDeselect={false}
                            value={availabilityB2B}
                            onChange={onAvailabilityB2BChange}
                            width="xl"
                            data={[
                                {
                                    value: 'follow',
                                    label: 'Suivre la quantité',
                                },
                                {
                                    value: 'force_available',
                                    label: 'Continuer à vendre en cas de rupture',
                                },
                                {
                                    value: 'force_disabled',
                                    label: 'Ne pas vendre',
                                },
                            ]}
                        />
                        {lastB2BChange && (
                            <span className="text-xs text-gray-500">
                                {dayjs(lastB2BChange.created_at).fromNow()} par{' '}
                                {lastB2BChange.user?.name}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <span className="font-medium">Date d&apos;expédition</span>

                    <div className="flex items-center justify-between">
                        {component.dispatch_date ? (
                            <span className="text-orange-500 font-medium">
                                {dayjs(component.dispatch_date).format('LL')}
                            </span>
                        ) : (
                            <span className="text-green-500">
                                Jour ouvré suivant
                            </span>
                        )}

                        <Button variant="default" onClick={handlersDateModale.open}>
                            Modifier
                        </Button>
                    </div>
                </div>

                {variants && variants.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <span className="font-medium">
                            Variantes Shopify ({variants.length})
                        </span>

                        <div className="flex flex-col border rounded-xl overflow-hidden">
                            <div className="grid grid-cols-[1fr_80px_80px_80px] divide-x w-full">
                                <span className="p-2">Variante</span>
                                <span className="p-2">Shopify</span>
                                <span className="p-2">B2C</span>
                                <span className="p-2">B2B</span>
                            </div>

                            <Spoiler
                                key={component.id}
                                showLabel="Afficher toutes les variantes..."
                                hideLabel="Masquer"
                            >
                                <div className="flex flex-col truncate">
                                    {variants.map((variant) => (
                                        <div
                                            className="grid grid-cols-[1fr_80px_80px_80px] divide-x w-full"
                                            key={variant.id}
                                        >
                                            <div className="flex flex-col items-start truncate p-2">
                                                <Anchor
                                                    className="truncate block"
                                                    href={
                                                        '/products/shopify/' +
                                                        variant.shopify_product_id +
                                                        '?variant_id=' +
                                                        variant.id
                                                    }
                                                >
                                                    {variant.title}
                                                </Anchor>

                                                <span className="text-gray-400 text-sm">
                                                    {variant.sku ?? 'Pas de SKU'}
                                                </span>
                                            </div>

                                            <div className="p-2">
                                                <QuantityDisplay
                                                    quantity={variant.shopify}
                                                    greenWhenPositive={true}
                                                />
                                            </div>

                                            <div className="p-2">
                                                <QuantityDisplay
                                                    quantity={variant.available_b2c}
                                                    greenWhenPositive={true}
                                                />
                                            </div>

                                            <div className="p-2">
                                                <QuantityDisplay
                                                    quantity={variant.available_b2b}
                                                    greenWhenPositive={true}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Spoiler>
                        </div>
                    </div>
                )}

                <pre className="hidden">
                    {JSON.stringify(variants, null, 2)}
                </pre>
            </div>

            <Modal
                opened={openedDateModale}
                onClose={handlersDateModale.close}
                title="Modifier la date d'expédition"
                centered
                withinPortal={true}
            >
                <div className="flex flex-col gap-4">
                    <DatePickerInput
                        locale="fr"
                        label="Date fixe"
                        valueFormat="LL"
                        clearable={true}
                        value={newDate}
                        onChange={setNewDate}
                        popoverProps={{
                            withinPortal: true,
                            position: 'bottom',
                        }}
                        placeholder="Selectionner une date..."
                    />
                    <Button onClick={saveNewDate} className="mx-auto">
                        Enregistrer
                    </Button>
                </div>
            </Modal>
        </Box>
    )
}
